import { StrictMode, Suspense, lazy, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import './styles/gise.css';
import { LangProvider } from './lib/i18n';
import { Toaster } from './lib/toast';
import { Home } from './screens/Home';
import { EventPage } from './screens/EventPage';
import { SeatMap } from './screens/SeatMap';
import { Checkout } from './screens/Checkout';
import { Confirm } from './screens/Confirm';
import { TicketView, TicketPdf } from './screens/Ticket';
import { MyTickets } from './screens/MyTickets';
import { Waiting } from './screens/Waiting';
import { Loading } from './components/AppHeader';

const Login = lazy(() => import('./screens/venue/Login').then((m) => ({ default: m.Login })));
const Dashboard = lazy(() => import('./screens/venue/Dashboard').then((m) => ({ default: m.Dashboard })));
const Editor = lazy(() => import('./screens/venue/Editor').then((m) => ({ default: m.Editor })));
const Staff = lazy(() => import('./screens/venue/Staff').then((m) => ({ default: m.Staff })));
const Scanner = lazy(() => import('./screens/venue/Scanner').then((m) => ({ default: m.Scanner })));
const DesignSystem = lazy(() => import('./screens/DesignSystem').then((m) => ({ default: m.DesignSystem })));

function NotFound() {
  return (
    <div className="scroll">
      <div className="center-msg">
        <div>
          <p className="cap muted">404</p>
          <h1 className="d-l" style={{ margin: '8px 0 16px' }}>Bu sahne boş.</h1>
          <a className="btn btn-primary" href="/">
            Keşfet’e dön
          </a>
        </div>
      </div>
    </div>
  );
}

/** Keep each route's scroll container at the top on navigation. */
function ScrollReset() {
  const loc = useLocation();
  useEffect(() => {
    document.querySelectorAll('.scroll').forEach((s) => (s.scrollTop = 0));
  }, [loc.pathname]);
  return null;
}

function App() {
  const loc = useLocation();
  const dark = loc.pathname.startsWith('/kapi');
  const paper2 = loc.pathname.endsWith('/pdf');
  return (
    <div className={`app${dark ? ' dark' : ''}${paper2 ? ' paper2' : ''}`}>
      <ScrollReset />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/e/:slug" element={<EventPage />} />
          <Route path="/koltuk/:sid" element={<SeatMap />} />
          <Route path="/odeme/:sid" element={<Checkout />} />
          <Route path="/sira/:sid" element={<Waiting />} />
          <Route path="/siparis/:id" element={<Confirm />} />
          <Route path="/bilet/:id" element={<TicketView />} />
          <Route path="/bilet/:id/pdf" element={<TicketPdf />} />
          <Route path="/biletlerim" element={<MyTickets />} />
          <Route path="/mekan" element={<Navigate to="/mekan/satis" replace />} />
          <Route path="/mekan/giris" element={<Login />} />
          <Route path="/mekan/satis" element={<Dashboard />} />
          <Route path="/mekan/harita" element={<Editor />} />
          <Route path="/mekan/ekip" element={<Staff />} />
          <Route path="/kapi" element={<Scanner />} />
          <Route path="/design" element={<DesignSystem />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Toaster />
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <LangProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </LangProvider>
  </StrictMode>,
);

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}
